import React, { Component, Fragment } from 'react'
import { TextLoader, Modal, Button, NavigationHeader } from '@driftt/tide-core'
import { Table } from '@driftt/tide-table'
import { clone, isEmpty } from 'ramda'
import { searchContactList, archiveContactGroup } from 'api/contacts'
import { getFilteredContacts, searchContacts } from 'api/segments'
import { queryTranslator } from 'utils/queryTranslator'
import { tracking } from '@driftt/traffic-control'
import classnames from 'classnames'
import { pluralize } from 'humanize-plus'

import ContactFilterBuilder from 'containers/ContactFilterBuilder'
import ContactControls from 'components/Contacts/ContactControls'
import ManageContacts from './ManageContacts'
import { RowFormatter } from 'components/Contacts/ContactList/RowFormatter'
import { Columns } from 'components/Contacts/ContactList/constants'
import Paginator from 'components/Paginator'
import ContactSidebar from 'components/Contacts/ContactSidebar'
import CreateNewContactSidebar from 'components/Contacts/CreateNewContactSidebar'
import { EmptyState } from '@driftt/crm-components'

import './style.css'
import ErrorBoundaryWithSentry from '../../utils/sentry/SentryErrorBoundary';

const WAIT_INTERVAL = 700

class ContactListContainer extends Component {
  constructor(props) {
    super(props)

    this.state = {
      contactsLoading: true,
      size: 25,
      index: 0,
      totalPages: null,
      currentPage: 0,
      isLoading: true,
      isFirstLoad: true,
      createNewContact: false,
      contactDetailSidebarOpen: false,
      segmentationSidebarOpen: props.location.search ? true : false,
      showDeleteConfirmationModal: false,
      isSorted: false,
      isSearching: false,
      currentFilter: {},
      sort: {direction: 'asc', property: 'name'},
      totalContacts: 0,
      contactId: props.match.params ? props.match.params.id : null
    }

    this.onSearch.bind(this)
  }

  componentWillMount() {
    this.timer = null
  }

  previousPage = () => {
    const { size, index } = this.state

    const newIndex = index - size

    this.setState(prevState => ({
      currentPage: prevState.currentPage - 1,
      index: newIndex,
      isLoading: true
    }), this.contextualGet)
  }

  nextPage = () => {
    const { size, index } = this.state

    const newIndex = index + size

    this.setState(prevState => {
      const nextPage = prevState.currentPage + 1
      return {
        currentPage: nextPage,
        index: newIndex,
        isLoading: true
      }
    }, this.contextualGet)
  }

  changeSize = ({ value }) => {
    this.setState({
      size: value,
      isLoading: true
    }, this.contextualGet)
  }

  componentDidMount() {
    tracking.trackEvent('Contact List - Page Viewed')
    const { contactId } = this.state

    if (contactId) {
      this.setState({
        contactDetailSidebarOpen:true
      })
    }

    this.getContacts()
  }

  getContacts = async () => {
    const { onReceiveContacts } = this.props

    const { index, size } = this.state

    try {
      const { data } = await searchContactList(size, index)

      onReceiveContacts(data.hydratedSegmentResults)

      this.setState({
        totalContacts: data.total,
        totalPages: Math.ceil(data.total / size),
        isFirstLoad: false,
        isLoading: false
      })

    } catch (error) {
      console.log(error)
    }
  }

  getFilteredContacts = async () => {
    const { currentFilter, index, size } = this.state

    const { onReceiveContacts } = this.props

    try {
      const { data } = await getFilteredContacts(currentFilter, size, index)

      onReceiveContacts(data.hydratedSegmentResults)

      this.setState({
        totalContacts: data.total,
        totalPages: Math.ceil(data.total / size),
        isLoading: false
      })
    } catch(e) {
      console.log(e)
    }
  }

  searchContacts = async () => {
    const {
      currentFilter,
      index,
      size,
      sort: { dir, property }
    } = this.state
    const { onReceiveContacts } = this.props

    const query = isEmpty(currentFilter) ? null : currentFilter
    const sortAscended = dir === 'asc' ? true : false

    try {
      const { data } = await searchContacts({ query, size, sortAscended, sortField: property, startFrom: index })

      onReceiveContacts(data.hydratedSegmentResults)

      this.setState({
        totalContacts: data.total,
        totalPages: Math.ceil(data.total / size),
        isLoading: false
      })
    } catch(e) {
      console.log(e)
    }
  }

  contextualGet = () => {
    const { currentFilter, isSorted } = this.state

    if (isSorted || currentFilter !== '') {
      this.searchContacts()
    } else {
      this.getContacts()
    }
  }

  setStickyHeader = (event) => {
    const { hasScrolled } = this.state

    const scrollingElement = event.target

    const offset = scrollingElement.scrollTop

    if (!hasScrolled && offset > 20) {
      this.setState({
        hasScrolled: true
      })
    } else if (hasScrolled && offset === 0) {
      this.setState({
        hasScrolled: false
      })
    }
  }

  toggleDeleteModal = () => {
    const { onDeselectContact, selectedContacts} = this.props

    this.setState(prevState => ({
      showDeleteConfirmationModal: !prevState.showDeleteConfirmationModal
    }), () => onDeselectContact(selectedContacts))
  }

  toggleContactDetailsSidebar = () => {
    const { history } = this.props

    history.push('/')

    this.setState(prevState => ({
      contactDetailSidebarOpen: !prevState.contactDetailSidebarOpen
    }))
  }

  toggleSegmentBuilderSidebar = () => {
    tracking.trackEvent('Contact List - Filter Builder Viewed')

    this.setState(prevState => ({
      segmentationSidebarOpen: !prevState.segmentationSidebarOpen
    }))
  }

  toggleNewContactSidebar = () => {
    this.setState(prevState => ({
      createNewContact: !prevState.createNewContact
    }))
  }

  getFormattedColumns = () => {
    const { allSelected, sort } = this.state

    return Columns(this.selectAll, allSelected, this.updateSortColumns)
      .map(column => {
        if (column.value === sort.property) {
          column.sorting = true
          column.direction = sort.dir
        } else {
          column.sorting = false
        }

        return column
      })
  }

  updateSortColumns = (value, direction) => {
    const getOppositeDirection = direction => {
      return direction === 'asc' ? 'desc' : 'asc'
    }

    this.setState({
      sort: {
        dir: getOppositeDirection(direction),
        property: value
      },
      isSorted: true,
      isLoading: true
    }, this.searchContacts)
  }

  onDelete = (selectedContact) => {
    const { onSelectContact } = this.props

    if (selectedContact) {
      onSelectContact(selectedContact)
    }

    this.setState({
      showDeleteConfirmationModal: true
    })
  }

  deleteContacts = async () => {
    const { selectedContacts, showTransientNotification, onDeselectContact } = this.props
    
    try {
      await archiveContactGroup(selectedContacts)

      onDeselectContact(selectedContacts)

      showTransientNotification({type: 'success', message: 'Successfully deleted contacts'})
      
      this.setState({
        showDeleteConfirmationModal: false,
        isLoading: true
      }, this.getContacts)
    } catch(e) {
      console.log(e)
      showTransientNotification({type: 'error', message: 'Sorry, an issue has occurred'})
    }
  }

  selectAll = (isSelected) => {
    const { contacts, onSelectContact, selectedContacts, onDeselectContact} = this.props

    const selected = clone(selectedContacts)

    contacts.forEach(contact => selected.push(contact.id))

    if (isSelected) {
      onSelectContact(selected)
    } else {
      onDeselectContact(selected)
    }

    this.setState({
      allSelected: isSelected
    })
  }

  goToContact = (contact) => {
    window.location.pathname = `/contacts/details/${contact}`
  }

  viewContactDetail = (id) => {
    const { history } = this.props
    const { contactId } = this.state

    history.push(`/${id}`)

    if (contactId !== id) {
      this.setState(prevState => ({
        contactId: id,
        contactDetailSidebarOpen: true
      }))
    } else {
      this.setState(prevState => ({
        contactId: id,
        contactDetailSidebarOpen: !prevState.contactDetailSidebarOpen
      }))
    }
  }

  selectContact = (id, isSelected) => {
    const { onSelectContact, onDeselectContact } = this.props

    if (isSelected) {
      onSelectContact(id)
    } else {
      onDeselectContact(id)
    }
  }

  handleSegmentSave = async (response) => {
    const { history } = this.props

    history.push(`/segments/${response.userListId}`)
  }

  handleSearchEntry = (event) => {
    clearTimeout(this.timer)

    this.setState({
      currentSearchTerm: event.target.value,
      isLoading: true
    })

    this.timer = setTimeout(this.onSearch, WAIT_INTERVAL);
  }

  onSearch = () => {
    const { currentSearchTerm } = this.state

    if (currentSearchTerm === '') {
      this.setState({
        currentFilter: {},
        isSearching: false,
        isLoading: true
      }, this.getContacts)
    } else {
      this.setState({
        currentFilter: currentSearchTerm,
        isSearching: true,
        isLoading: true,
        index: 0,
        currentPage: 0
      }, this.searchContacts)
    }
  }

  updateNewQuery = (query) => {
    this.setState({
      currentFilter: query,
      isLoading: true,
      isFiltering: true
    }, this.getFilteredContacts)
  }

  updateQuery = (query, bypassTranslate) => {
    const reformattedQuery = bypassTranslate ? query : queryTranslator(query)

    this.setState({
      currentFilter: reformattedQuery,
      isLoading: true,
      isFiltering: true
    }, this.getFilteredContacts)
  }

  formatDataForTable = () => {
    const { contacts } = this.props

    return contacts.map((contact) => RowFormatter(contact, this.onDelete, this.viewContactDetail, this.selectContact, this.props.selectedContacts, this.goToContact))
  }

  renderLoading = () => {
    const { isFirstLoad } = this.state
    return (
      <Fragment>
        <div className="contacts-list-table-container">
          <TextLoader text={'Grabbing your contacts'} hasFinished={!isFirstLoad} className="contacts-list-table-container--loading"/>
        </div>
      </Fragment>
    )
  }

  render() {
    const { 
      totalPages, 
      size, 
      currentPage, 
      isLoading, 
      isSearching,
      isFiltering,
      isFirstLoad, 
      contactDetailSidebarOpen,
      segmentationSidebarOpen,
      contactId,
      totalContacts,
      createNewContact,
      hasScrolled,
      showDeleteConfirmationModal
    } = this.state
    
    const { contacts, history, selectedContacts } = this.props

    if (isFirstLoad) {
      return this.renderLoading()
    }

    return (
      <ErrorBoundaryWithSentry>
        <NavigationHeader title="All contacts">
          <ManageContacts
            addNew={this.toggleNewContactSidebar}
            resetContacts={this.getContacts}
            allContacts={totalContacts}
          />
        </NavigationHeader>
        <div className="contacts-container">
          <ContactControls
            showSegmentation={this.toggleSegmentBuilderSidebar}
            contacts={totalContacts}
            selectAll={this.selectAll}
            onChange={this.handleSearchEntry}
            deleteContacts={this.onDelete}
          />
          <div
            className={classnames('contacts-list-table-container', { 'contacts-list-table--stick': hasScrolled })}
            onScroll={this.setStickyHeader}
          >
            {!!contacts &&
              <Table
                columns={this.getFormattedColumns()}
                data={this.formatDataForTable()}
                isWorking={isLoading}
                className="contacts-list-table"
              />
            }
            {(contacts.length === 0 && !isFirstLoad && !isLoading && (isSearching || isFiltering)) &&
              <EmptyState topText="We couldn’t find any contacts matching your filters" bottomText="Try broadening your filters or search terms to see results here." />
            }
            {(contacts.length === 0 && !isFirstLoad && !isLoading && !isSearching && !isFiltering) &&
              <EmptyState history={history} topText="You currently have no contacts." bottomText="Click below to upload your contacts CSV" ctaUrl="/imports/upload" ctaText="Upload a CSV" />
            }
            {totalContacts === 0 || (!!contacts &&
              <Paginator
                totalPages={totalPages}
                size={size}
                goBackward={this.previousPage}
                goForward={this.nextPage}
                changeSize={this.changeSize}
                index={currentPage}
              />)
            }
            {!!createNewContact &&
              <CreateNewContactSidebar
                close={this.toggleNewContactSidebar}
                onSave={this.createNewContact}
                open={createNewContact}
              />
            }
            {!!contactId &&
              <ContactSidebar
                id={contactId}
                close={this.toggleContactDetailsSidebar}
                open={contactDetailSidebarOpen}
              />
            }
            <ContactFilterBuilder
              open={segmentationSidebarOpen}
              close={this.toggleSegmentBuilderSidebar}
              onSave={this.handleSegmentSave}
              onUpdate={this.updateNewQuery}
            />
            <Modal
              open={showDeleteConfirmationModal}
              type="warning"
              size="small"
              title="Are you sure?"
              className="contacts-list-delete-confirmation-modal"
              onClose={this.toggleDeleteModal}
            >
              <div className="contacts-list-delete-confirmation">
                <p>
                  {`You are about to delete ${selectedContacts.length} ${pluralize(selectedContacts.length, 'contact')}.`}
                </p>
                <p>
                  {`This action cannot be undone.`}
                </p>
              </div>
              <footer className="contact-list-delete-footer">
                <Button type="secondary" onClick={this.deleteContacts}>{`Delete ${selectedContacts.length} ${pluralize(selectedContacts.length, 'contact')}`}</Button>
                <Button type="tertiary" onClick={this.toggleDeleteModal}>Cancel</Button>
              </footer>
            </Modal>
          </div>
        </div>
      </ErrorBoundaryWithSentry>
    )
  }
}

export default ContactListContainer
